import type { LoginActivity, PdSession } from 'src/app/core/services/admin-session-api.service';

/** Which kind of audit record a view is showing; '' is the merged timeline. */
export type AuditEntryType = '' | 'session' | 'login' | 'event';
import { trimmedOrUndefined } from 'src/app/shared/utils/string.util';

export function loginActivityUserName(login: LoginActivity): string {
  return trimmedOrUndefined(login.user?.full_name) ?? login.user?.email_id ?? '—';
}

export function loginActivityUserEmail(login: LoginActivity): string {
  return login.user?.email_id ?? '—';
}

export function loginActivityStatusLabel(login: LoginActivity): string {
  const s = (login.status ?? '').toLowerCase();
  if (s === 'success') return 'Success';
  if (s === 'failed') return 'Failed';
  return login.status ?? '—';
}

export function loginActivityStatusClass(login: LoginActivity): string {
  const s = (login.status ?? '').toLowerCase();
  if (s === 'success') return 'text-emerald-700 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-950/50';
  if (s === 'failed') return 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-950/50';
  return 'text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-white/5';
}

export function loginActivityRuntimeDisplay(login: LoginActivity): string {
  const parts = [login.browser, login.platform, login.device].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

// ── PD session display ───────────────────────────────────────────────────────
// Shared by the list table, the CSV export and the session detail page so the
// three stay in agreement about what a session is called and how it scored.

export function sessionType(s: PdSession): string {
  const src = String(s.metadata?.['source'] ?? '');
  if (src === 'kyc_simulate' || src === 'simulate') return 'KYC Bot';
  if (s.ai_response?.pan || s.ai_response?.face) return 'KYC Bot';
  return 'PD Bot';
}

function conversationLength(s: PdSession): number {
  let raw: unknown = s.conversation_log;
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw); } catch { raw = []; }
  }
  return Array.isArray(raw) ? raw.length : 0;
}

/**
 * Whether this session had a live interview, so the Transcript tab and the Live Video card mean
 * something. The workflow that ran decides when it left a record (an INTERVIEW step); otherwise the
 * session's own evidence does — a conversation, interview answers, a video room or a recording. A KYC
 * rehearsal that never interviewed anyone therefore shows neither. A PD session with no workflow
 * record keeps both, because that is where the operator starts the interview room.
 */
export function sessionHadInterview(s: PdSession, sections: readonly { node_code: string }[] | undefined): boolean {
  const ran = !!sections?.length;
  if (ran && sections.some((x) => x.node_code === 'INTERVIEW')) return true;
  const evidence =
    conversationLength(s) > 0 ||
    (s.ai_response?.answer_scores?.length ?? 0) > 0 ||
    !!s.metadata?.video_room_url ||
    !!s.metadata?.has_video_recording;
  if (evidence) return true;
  return !ran && sessionType(s) === 'PD Bot';
}

export function sessionAgentName(s: PdSession): string {
  return s.agent?.name ?? `Agent #${s.agent_id}`;
}

export function sessionEventLabel(s: PdSession): string {
  if (s.ai_response?.pan || s.ai_response?.face) return 'KYC Verification';
  const src = String(s.metadata?.['source'] ?? '');
  if (src === 'kyc_simulate' || src === 'simulate') return 'KYC Verification';
  return s.status ?? 'Session';
}

/** Column result, else the AI verdict, else derived from the score. */
function sessionResult(s: PdSession): string | null {
  const col = (s.result ?? '').trim().toUpperCase();
  if (col) return col;
  const fromAi = (s.ai_response?.result ?? '').trim().toUpperCase();
  if (fromAi === 'PASS' || fromAi === 'REVIEW' || fromAi === 'FAIL') return fromAi;
  const score = s.ai_response?.overall_score ?? s.ai_response?.composite_score ?? s.score;
  if (score != null) {
    const n = Number(score);
    if (!Number.isNaN(n)) {
      if (n >= 70) return 'PASS';
      if (n >= 40) return 'REVIEW';
      return 'FAIL';
    }
  }
  return null;
}

export function sessionStatusLabel(s: PdSession): 'PASS' | 'REVIEW' | 'FAIL' | 'PENDING' {
  const r = sessionResult(s);
  if (r === 'PASS') return 'PASS';
  if (r === 'REVIEW') return 'REVIEW';
  if (r === 'FAIL') return 'FAIL';
  return 'PENDING';
}

export function sessionStatusClass(s: PdSession): string {
  const r = sessionStatusLabel(s);
  if (r === 'PASS') return 'text-emerald-700 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-950/50';
  if (r === 'REVIEW') return 'text-amber-700 bg-amber-100 dark:text-amber-200 dark:bg-amber-950/50';
  if (r === 'FAIL') return 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-950/50';
  return 'text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-white/5';
}

export function sessionScoreDisplay(s: PdSession): string {
  const score = s.ai_response?.overall_score ?? s.ai_response?.composite_score ?? s.score;
  if (score == null) return '—';
  return `${Math.round(Number(score))}`;
}

export function sessionGeoDisplay(s: PdSession): string {
  const m = s.metadata;
  if (m?.geo_lat != null && m?.geo_lng != null) {
    return `${Number(m.geo_lat).toFixed(4)}, ${Number(m.geo_lng).toFixed(4)}`;
  }
  return '—';
}

export function isoDateDisplay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function isoTimeDisplay(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}
